import { useTranslation } from 'react-i18next';

import ArrowRightShape from '@/components/common/svg/shapes/ArrowRightShape.tsx';
import {
  menuItemStyle,
  personalBlockContainer,
  afterStyle,
  iconWrapperStyle,
  menuTextStyle
} from '@/components/ProfileData/Blocks/styles.ts';
import SpokenLanguagesShape from '@/components/common/svg/shapes/SpokenLanguagesShape.tsx';
import BodyShape from '@/components/common/svg/shapes/BodyShape.tsx';
import BriefcaseShape from '@/components/common/svg/shapes/BriefcaseShape.tsx';
import ZodiacShape from '@/components/common/svg/shapes/ZodiacShape.tsx';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { getBodyTypes } from '@/components/Popup/EditProfile/EditBodyTypePopup.tsx';
import { getEmploymentSectors } from '@/components/Popup/EditProfile/EditEmploymentPopup.tsx';
import { getSpokenLanguages } from '@/components/Popup/EditProfile/EditSpokenLanguagesPopup.tsx';
import { getZodiacSigns } from '@/components/Popup/EditProfile/EditZodiacPopup.tsx';

interface PersonalBlockProps {
  onOpenEmployment: () => void;
  onOpenBodyType: () => void;
  onOpenZodiac: () => void;
  onOpenSpokenLanguages: () => void;
}

export const PersonalBlock = ({
  onOpenEmployment,
  onOpenBodyType,
  onOpenZodiac,
  onOpenSpokenLanguages
}: PersonalBlockProps) => {
  const { t } = useTranslation();
  const { aboutMe } = useProfileStore();
  const bodyTypes = getBodyTypes(t);
  const employmentSectors = getEmploymentSectors(t);
  const zodiacSigns = getZodiacSigns(t);
  const spokenLanguages = getSpokenLanguages(t);

  const showSpokenLanguages = () => {
    if (
      !aboutMe?.profile.spoken_languages ||
      aboutMe.profile.spoken_languages.length === 0
    ) {
      return t('status_unspecified');
    }

    const translatedLanguages = aboutMe.profile.spoken_languages
      .slice(0, 2)
      .map((lang) => spokenLanguages[lang])
      .filter(Boolean);

    const moreCount = aboutMe.profile.spoken_languages.length - 2;

    return moreCount > 0
      ? `${translatedLanguages.join(', ')} ${t('language.and_more', { count: moreCount })}`
      : translatedLanguages.join(', ');
  };

  const menuItems = [
    {
      shape: <BriefcaseShape width={20} height={20} fillColor="#1A1A1A" />,
      onClick: onOpenEmployment,
      labelKey: 'employment_title',
      after: aboutMe?.profile.employment_sector
        ? employmentSectors[aboutMe?.profile.employment_sector]
        : ''
    },
    {
      shape: <BodyShape width={20} height={20} fillColor="#1A1A1A" />,
      onClick: onOpenBodyType,
      labelKey: 'body_type_title',
      after: aboutMe?.profile.body_type
        ? bodyTypes[aboutMe?.profile.body_type]
        : ''
    },
    {
      shape: <ZodiacShape width={20} height={20} fillColor="#1A1A1A" />,
      onClick: onOpenZodiac,
      labelKey: 'zodiac_title',
      after: aboutMe?.profile.zodiac_sign
        ? zodiacSigns[aboutMe?.profile.zodiac_sign]
        : ''
    },
    {
      shape: (
        <SpokenLanguagesShape width={20} height={20} fillColor="#1A1A1A" />
      ),
      onClick: onOpenSpokenLanguages,
      labelKey: 'spoken_languages_title',
      after:
        aboutMe?.profile.spoken_languages.length !== 0
          ? showSpokenLanguages()
          : t('status_unspecified')
    }
  ];

  return (
    <div style={personalBlockContainer}>
      {menuItems.map(({ shape, onClick, labelKey, after }) => (
        <div key={labelKey} style={menuItemStyle} onClick={onClick}>
          <div style={iconWrapperStyle}>{shape}</div>
          <div style={{ flexGrow: 1, textAlign: 'left' }}>
            <span style={menuTextStyle}>{t(labelKey)}</span>
            {after && <div style={afterStyle}>{after || t('add')}</div>}
          </div>
          <ArrowRightShape width={14} height={14} fillColor="#C8C8CB" />
        </div>
      ))}
    </div>
  );
};
